// Site-wide input masks: formats CPF, CNPJ, phone (DDD), CEP, card number, card expiry and dd/mm/aaaa dates while typing.
// Runs in the window capture phase, before React reads the value, so controlled inputs keep working without per-form code.
const d = (v) => v.replace(/\D/g, '');

const fmt = {
  cpf: (v) => d(v).slice(0, 11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2'),
  cnpj: (v) => d(v).slice(0, 14).replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2'),
  cpfCnpj: (v) => (d(v).length > 11 ? fmt.cnpj(v) : fmt.cpf(v)),
  phone: (v) => {
    const x = d(v).replace(/^55(?=\d{10,11}$)/, '').slice(0, 11);
    if (x.length <= 2) return x.length ? `(${x}` : '';
    if (x.length <= 6) return `(${x.slice(0, 2)}) ${x.slice(2)}`;
    if (x.length <= 10) return `(${x.slice(0, 2)}) ${x.slice(2, 6)}-${x.slice(6)}`;
    return `(${x.slice(0, 2)}) ${x.slice(2, 7)}-${x.slice(7)}`;
  },
  cep: (v) => d(v).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2'),
  card: (v) => d(v).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 '),
  expiry: (v) => d(v).slice(0, 4).replace(/(\d{2})(\d)/, '$1/$2'),
  date: (v) => d(v).slice(0, 8).replace(/(\d{2})(\d)/, '$1/$2').replace(/(\d{2})\/(\d{2})(\d)/, '$1/$2/$3'),
};

export const maskAs = (kind, v) => (fmt[kind] ? fmt[kind](v || '') : v);

export const isValidCpf = (v) => {
  const c = d(v || '');
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  for (const size of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < size; i += 1) sum += Number(c[i]) * (size + 1 - i);
    const dv = (sum * 10) % 11 % 10;
    if (dv !== Number(c[size])) return false;
  }
  return true;
};

export const isValidCnpj = (v) => {
  const c = d(v || '');
  if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
  for (const size of [12, 13]) {
    const w = size === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = w.reduce((acc, x, i) => acc + Number(c[i]) * x, 0) % 11;
    if ((sum < 2 ? 0 : 11 - sum) !== Number(c[size])) return false;
  }
  return true;
};

const DOC_CHECK = {
  cpf: [isValidCpf, 'CPF inválido. Confira os números digitados.'],
  cnpj: [isValidCnpj, 'CNPJ inválido. Confira os números digitados.'],
  cpfCnpj: [(v) => isValidCpf(v) || isValidCnpj(v), 'CPF/CNPJ inválido. Confira os números digitados.'],
};

export const docError = (kind, v) => (v && DOC_CHECK[kind] && !DOC_CHECK[kind][0](v) ? DOC_CHECK[kind][1] : '');

const RULES = [
  [/cpf_?cnpj|cpfcnpj|pix-cpf|plan-pix-cpf|payer-doc/i, 'cpfCnpj'],
  [/cnpj/i, 'cnpj'],
  [/cpf/i, 'cpf'],
  [/telefone|phone|celular|whatsapp/i, 'phone'],
  [/(^|[-_\s])cep|[a-z]Cep(?![a-z])|postal/i, 'cep'],
  [/card-number|cardnumber/i, 'card'],
  [/card-expiry|expiry|validade/i, 'expiry'],
];

const kindOf = (el) => {
  if (el.dataset.mask) return el.dataset.mask === 'none' ? null : el.dataset.mask;
  if (el.type !== 'text' && el.type !== 'tel' && el.type !== 'search' && el.getAttribute('type') !== null) return null;
  const key = `${el.name || ''} ${el.id || ''} ${el.dataset.testid || ''}`;
  const hit = RULES.find(([re]) => re.test(key));
  return hit ? hit[1] : null;
};

const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

export const installAutoMask = () => {
  window.addEventListener('input', (e) => {
    const el = e.target;
    if (!(el instanceof window.HTMLInputElement)) return;
    const kind = kindOf(el);
    if (!kind || !fmt[kind]) return;
    if (DOC_CHECK[kind]) el.setCustomValidity('');
    const next = fmt[kind](el.value);
    if (next !== el.value) setter.call(el, next);
  }, true);
  // Blocks any form submit while a CPF/CNPJ field holds an invalid number
  window.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof window.HTMLFormElement)) return;
    const bad = [...form.querySelectorAll('input')].find((el) => docError(kindOf(el), el.value.trim()));
    if (!bad) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    bad.setCustomValidity(docError(kindOf(bad), bad.value.trim()));
    bad.reportValidity();
    bad.focus();
  }, true);
};
